import { Controller } from 'front-end-core/core/Router'
import { combineLatest, of } from 'rxjs'
import { startWith, catchError, takeUntil, debounceTime, skipWhile, first, filter, map } from 'rxjs/operators'
import { requestReferences } from 'services/LegislationReferenceService'
import {
  expandCallback,
  expandContentPath,
  requestGroup,
  requestSectionContent,
  requestSections,
  selectCallback
} from 'services/QAService'
import Main from './Main'
import Right from './Right'
import Header from './Header'
import { RESPONSE_BADDATA, RESPONSE_PENDING } from 'front-end-core/constants/responses'
import { isValidResponse } from 'front-end-core/helpers/dataflow'
import getUrl from 'front-end-core/core/url'
import { QA_CONTENT, QA_MENU } from 'constants/controllers'
import debug from 'front-end-core/core/debug'
import { invert, result } from 'libraries/lodash'

export default new Controller({
  name: QA_CONTENT,
  route: '(:aol/):flavour/qa/:sectionId(/:focusedId(/:highlight))',
  controller (Router, evt, render) {
    const { aol, flavour, focusedId = null, sectionId, highlight = null } = evt.params
    const { scrollMain, pageView, pageData = {}, pageState = {}, previousPageId } = evt
    const pageId = getUrl(QA_CONTENT, {
      aol,
      flavour,
      sectionId,
      ...(focusedId !== null && { focusedId }),
      ...(highlight !== null && { highlight })
    })
    const layoutData = {
      bottleLink: (flavour) => getUrl(QA_MENU, { aol, flavour }),
      flavour,
      aol,
      className: 'flavoured',
      pageId,
      pageView,
      searchType: 'qa',
      sectionId
    }
    const eventCategory = 'Q&A'

    const [expanded] = (invert(pageState)['1'] || '').split('/')

    const url = getUrl(QA_CONTENT, { aol, flavour, sectionId, focusedId }).replace(/#/, '')

    if (pageData.references) {
      requestReferences(flavour, pageData.references, aol)
    }

    const cxNames = {
      groupHeader: {
        onHoverColor: `hover-bg-flavour-${flavour}`,
        depthStyle: (depth) => {
          switch (depth) {
            case 1:
              return `bg-white b--flavour-${flavour} hover-bg-flavour-${flavour}`
            case 2:
              return `bg-flavour-${flavour} b--flavour-${flavour}`
            default:
              return `bg-twilight-grey ${flavour} b--flavour-${flavour}`
          }
        },
        iconDepthStyle: () => 'cadet-blue'
      },
      borderColor: `b--flavour-${flavour}`,
      content: {
        divider: `btc--flavour-${flavour}`
      },
      contentHeader: {
        headerBackground: 'bg-white'
      }
    }

    const loadData = (highlight) => combineLatest(
      [
        requestGroup({ flavour, parentId: sectionId, aol }),
        requestSections(flavour, aol).pipe(map(s => result(s, ['nodes', sectionId, 'title'])))
      ],
      (group, section) => ({ group, section, title: null })
    ).pipe(
      startWith({ group: RESPONSE_PENDING, title: null }),
      catchError(
        e => {
          debug.warn(`Bad data ${e.message}`)
          return of({ group: RESPONSE_BADDATA, title: null })
        }
      ),
      takeUntil(Router)
    )
      .subscribe(
        (pageData) =>
          render(
            { Header, Main, Right },
            Object.assign({ expandCallback, selectCallback, eventCategory, aol, highlight, sectionId, cxNames }, pageData),
            layoutData
          )
      )

    const renderInvalid = (resp) => render({ Main }, { flavour, group: resp }, layoutData)
    const preload = (parentId, then) => {
      requestSectionContent(flavour, parentId, aol).pipe(
        startWith(RESPONSE_PENDING),
        debounceTime(5),
        skipWhile(r => ((!isValidResponse(r) && renderInvalid(r)) || !isValidResponse(r))), // TODO: work out what the expected out put of this bizarre line actually is
        first(),
        takeUntil(Router)
      ).subscribe(then)
    }

    const whenContentLoads = (then) => requestSectionContent(flavour, sectionId, aol).pipe(
      filter(isValidResponse),
      first()
    ).subscribe(then)

    // TODO: check how much of this is still needed, scrolling at least appears to be handled elsewhere
    if (previousPageId !== pageId) {
      // Scroll to content
      if (sectionId) {
        preload(sectionId, () => {
          expandContentPath(flavour, sectionId, focusedId || sectionId, aol).subscribe()
          loadData('#' + url)
        })
      } else if (expanded && scrollMain !== null) {
        preload(expanded, () => {
          loadData()
        })
      } else if (scrollMain !== null) {
        loadData()
      } else {
        loadData()
      }
    } else {
      if (expanded && scrollMain) {
        whenContentLoads(() => {
          // required for clicking back (FC-1031), AccordionWrapper props needs updating
          expandContentPath(flavour, sectionId, focusedId || sectionId, aol).subscribe()
          loadData('#' + url)
        })
      } else if (sectionId) {
        expandContentPath(flavour, sectionId, focusedId || sectionId, aol).subscribe()
        if (focusedId) {
          loadData('#'.concat(url))
        }
      }
    }
    return pageId
  }
})
